import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Expand } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal, Eyebrow } from "./Reveal";

const JOBS = [
  { src: "/images/hot-water-install-1.jpg", title: "Heat pump hot water", tag: "Hot Water" },
  { src: "/images/filtration-install.jpg", title: "Whole-house water filtration", tag: "Filtration" },
  { src: "/images/hot-water-install-2.jpg", title: "iStore heat pump install", tag: "Hot Water" },
];
const SLIDES = [...JOBS, ...JOBS];

const NavBtn = ({ onClick, label, children, testid }) => (
  <button onClick={onClick} aria-label={label} data-testid={testid}
    className="grid h-14 w-14 place-items-center rounded-full border border-[#0F2B5B]/20 text-[#0F2B5B] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#FF6B00] hover:text-white">
    {children}
  </button>
);

export const Gallery = () => {
  const [ref, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!embla) return undefined;
    const onSelect = () => setIndex(embla.selectedScrollSnap() % JOBS.length);
    embla.on("select", onSelect);
    onSelect();
    return () => { embla.off("select", onSelect); };
  }, [embla]);

  const prev = useCallback(() => embla?.scrollPrev(), [embla]);
  const next = useCallback(() => embla?.scrollNext(), [embla]);

  return (
    <section id="our-work" className="overflow-hidden bg-white py-24 lg:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <Eyebrow>Recent jobs</Eyebrow>
            <h2 className="font-display max-w-2xl text-4xl font-extrabold leading-[1] tracking-tight text-[#0F2B5B] sm:text-6xl">Real installs. Done properly.</h2>
            <p className="mt-4 max-w-md text-base text-slate-600 md:text-lg">Swipe through some of our recent work across Perth.</p>
          </Reveal>
          <div className="flex items-center gap-4">
            <span className="font-cond text-lg font-bold tracking-[0.14em] text-[#0F2B5B]" data-testid="gallery-counter">
              0{index + 1} <span className="text-slate-500">/ 0{JOBS.length}</span>
            </span>
            <NavBtn onClick={prev} label="Previous photo" testid="gallery-prev-button"><ArrowLeft className="h-5 w-5" /></NavBtn>
            <NavBtn onClick={next} label="Next photo" testid="gallery-next-button"><ArrowRight className="h-5 w-5" /></NavBtn>
          </div>
        </div>
        <div ref={ref} className="cursor-grab active:cursor-grabbing" data-testid="gallery-carousel">
          <div className="-ml-5 flex touch-pan-y">
            {SLIDES.map((j, i) => (
              <div key={i} className="min-w-0 shrink-0 grow-0 basis-[85%] pl-5 sm:basis-1/2 lg:basis-1/3">
                <button onClick={() => setOpen(j)} data-testid={`gallery-slide-${i}`}
                  className="group relative block aspect-[4/5] w-full overflow-hidden rounded-[1.75rem] text-left">
                  <img src={j.src} alt={`${j.title} by Plumberoo`} draggable="false" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F42]/90 via-transparent to-transparent" />
                  <span className="absolute left-5 top-5 rounded-full bg-[#FF6B00] px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-white">{j.tag}</span>
                  <span className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-[#0F2B5B] opacity-0 transition-opacity duration-300 group-hover:opacity-100"><Expand className="h-4 w-4" /></span>
                  <p className="font-display absolute bottom-6 left-6 right-6 text-2xl font-extrabold text-white">{j.title}</p>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-8 flex justify-center gap-2" data-testid="gallery-dots">
          {JOBS.map((_, i) => (
            <button key={i} onClick={() => embla?.scrollTo(i)} aria-label={`Go to photo ${i + 1}`} data-testid={`gallery-dot-${i}`}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${index === i ? "w-10 bg-[#FF6B00]" : "w-2 bg-slate-300"}`} />
          ))}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl border-0 bg-transparent p-0 shadow-none" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{open?.title}</DialogTitle>
          {open && <img src={open.src} alt={`${open.title} by Plumberoo`} className="max-h-[85vh] w-full rounded-2xl object-contain" />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
